import { BaseApiService } from '../base/BaseApiService';
import {
  CreateClientRequest,
  UpdateClientRequest,
  ClientResponse,
  ClientListResponse,
  ClientContractsSummaryResponse,
  ClientListFiltersRequest,
  ClientListPagedResponse
} from '../../types/dtos';
import { ApiResponse } from '../../types/api';
import { ClientObservationEntryDto } from '../contracts/ContractService';
import { isCanceledError, isTimeoutError, withRequestTimeout } from '../utils/apiErrorUtils';

export const CNPJ_SEARCH_TIMEOUT_MS = 10_000;
export const CNPJ_SEARCH_TIMEOUT_MESSAGE =
  'A consulta ao CNPJ demorou demais. Tente novamente.';

/** null = cancelamento silencioso (unmount / consulta substituída). */
export function getCnpjSearchUserMessage(err: unknown): string | null {
  if (isCanceledError(err)) return null;
  if (isTimeoutError(err)) return CNPJ_SEARCH_TIMEOUT_MESSAGE;
  if (err instanceof Error && err.message.trim()) return err.message;
  return 'Erro ao buscar dados do CNPJ';
}

export interface CreateClientObservationRequest {
  clientId: string;
  occurrence?: string;
  observation?: string;
  sourceType?: string;
  contractId?: string;
}

// DTO para dados da ReceitaWS
export interface ReceitaWsCompanyData {
  cnpj?: string;
  nome?: string;
  fantasia?: string;
  tipo?: string;
  porte?: string;
  abertura?: string;
  situacao?: string;
  motivoSituacaoCadastral?: string;
  dataSituacao?: string;
  situacaoEspecial?: string;
  dataSituacaoEspecial?: string;
  naturezaJuridica?: string;
  capitalSocial?: number;
  atividadePrincipal?: Array<{ code: string; text: string }>;
  atividadesSecundarias?: Array<{ code: string; text: string }>;
  logradouro?: string;
  numero?: string;
  complemento?: string;
  cep?: string;
  bairro?: string;
  municipio?: string;
  uf?: string;
  email?: string;
  telefone?: string;
  efr?: string;
  simples?: {
    optante: boolean;
    dataOpcao?: string;
    dataExclusao?: string;
  };
  simei?: {
    optante: boolean;
    dataOpcao?: string;
    dataExclusao?: string;
  };
  qsa?: Array<{
    nome?: string;
    qual?: string;
    paisOrigem?: string;
    nomeRepLegal?: string;
    qualRepLegal?: string;
  }>;
  status?: string;
  message?: string;
  ultimaAtualizacao?: string;
}

// DTO para resposta do processamento de CNPJ
export interface ProcessCnpjResult {
  cnpj: string;
  success: boolean;
  message: string;
}

// DTO para requisição de processamento múltiplo
export interface ProcessMultipleCnpjsRequest {
  cnpjs: string[];
}

export interface SearchAndSaveClientRequest {
  cnpj: string;
}

// DTO para resposta da API de processamento
export interface ProcessMultipleCnpjsResponse {
  success: boolean;
  message: string;
  data: ProcessCnpjResult[];
}

export interface SearchAndSaveClientResponse {
  success: boolean;
  message: string;
  data: ClientResponse;
}

export interface MeuTimHealthCheckBulkEnqueueItem {
  clientId: string;
  enqueued: boolean;
  message: string;
}

export interface MeuTimHealthCheckBulkEnqueueResponse {
  requested: number;
  enqueued: number;
  skipped: number;
  items: MeuTimHealthCheckBulkEnqueueItem[];
}

/**
 * Status reais do verify Meu TIM on-demand (fatia 0 GCM). `nao_testada` é o
 * valor default quando o cliente nunca foi verificado; os demais vêm do
 * worker/portal Meu TIM. Nunca existe status de "enfileirado com sucesso".
 */
export type MeuTimHealthCheckStatus =
  | 'ok'
  | 'credencial_invalida'
  | 'bloqueio_ou_captcha'
  | 'erro_portal'
  | 'timeout'
  | 'nao_testada';

export interface MeuTimHealthCheckOnDemandResponse {
  clientId: string;
  status: MeuTimHealthCheckStatus;
  message: string | null;
  checkedAt: string;
}

export class ClientService extends BaseApiService {
  async getClients(search?: string): Promise<ClientListResponse[]> {
    const params = search ? { search } : undefined;
    const response = await this.api.get<ApiResponse<ClientListResponse[]>>('/clients', { params });
    return this.extractData(response);
  }

  async getClientsPaged(filters: ClientListFiltersRequest): Promise<ClientListPagedResponse> {
    const params = new URLSearchParams();
    if (filters.search) params.append('search', filters.search);
    if (filters.situacaoCadastral) params.append('situacaoCadastral', filters.situacaoCadastral);
    if (filters.cidade) params.append('cidade', filters.cidade);
    if (filters.estado) params.append('estado', filters.estado);
    if (filters.clientClassification) {
      params.append('clientClassification', filters.clientClassification);
      params.append('classification', filters.clientClassification);
    }
    if (filters.hasContract !== undefined) params.append('hasContract', String(filters.hasContract));
    if (filters.meuTimStatus) params.append('meuTimStatus', filters.meuTimStatus);
    if (filters.pageNumber) params.append('pageNumber', String(filters.pageNumber));
    if (filters.pageSize) params.append('pageSize', String(filters.pageSize));

    const queryString = params.toString();
    const response = await this.api.get<ApiResponse<ClientListPagedResponse> | ClientListPagedResponse>(
      `/clients${queryString ? `?${queryString}` : ''}`
    );
    return this.extractData(response);
  }

  async getClientById(id: string): Promise<ClientResponse> {
    const response = await this.api.get<ApiResponse<ClientResponse>>(`/clients/${id}`);
    return this.extractData(response);
  }

  async getClientMeuTimCredentials(clientId: string): Promise<{ meuTimLogin: string | null; meuTimSenha: string | null }> {
    const response = await this.api.get<{ meuTimLogin: string | null; meuTimSenha: string | null }>(
      `/clients/${clientId}/meu-tim-credentials`
    );
    return response.data;
  }

  async updateClientMeuTimCredentials(
    clientId: string,
    body: { meuTimLogin: string; meuTimSenha: string }
  ): Promise<void> {
    const response = await this.api.put<{ success?: boolean; message?: string }>(
      `/clients/${clientId}/meu-tim-credentials`,
      body
    );
    const d = response.data;
    if (d && typeof d === 'object' && 'success' in d && d.success === false) {
      throw new Error(d.message || 'Erro ao salvar credenciais Meu TIM');
    }
  }

  async getClientsContractsSummary(clientIds?: string[]): Promise<ClientContractsSummaryResponse[]> {
    const params = new URLSearchParams();
    if (clientIds && clientIds.length > 0) {
      clientIds.forEach((id) => params.append('clientIds', id));
    }

    const queryString = params.toString();
    const response = await this.api.get<ApiResponse<ClientContractsSummaryResponse[]>>(
      `/clients/contracts-summary${queryString ? `?${queryString}` : ''}`
    );
    return this.extractData(response);
  }

  async createClient(clientData: CreateClientRequest): Promise<ClientResponse> {
    const response = await this.api.post<ApiResponse<ClientResponse>>('/clients', clientData);
    return this.extractData(response);
  }

  async updateClient(id: string, clientData: UpdateClientRequest): Promise<ClientResponse> {
    const response = await this.api.put<ApiResponse<ClientResponse>>(`/clients/${id}`, clientData);
    return this.extractData(response);
  }

  async getClientObservations(clientId: string): Promise<ClientObservationEntryDto[]> {
    const response = await this.api.get<ApiResponse<ClientObservationEntryDto[]>>(
      `/clients/${clientId}/observations`
    );
    return this.extractData(response);
  }

  async addClientObservation(
    clientId: string,
    body: CreateClientObservationRequest
  ): Promise<ClientObservationEntryDto> {
    const response = await this.api.post<ApiResponse<ClientObservationEntryDto>>(
      `/clients/${clientId}/observations`,
      body
    );
    return this.extractData(response);
  }

  async deleteClient(id: string): Promise<void> {
    const response = await this.api.delete(`/clients/${id}`);
    if (response.data && typeof response.data === 'object' && 'success' in response.data) {
      if (!response.data.success) {
        throw new Error(response.data.message || 'Erro ao excluir cliente');
      }
    }
  }

  async activateClient(id: string): Promise<void> {
    const response = await this.api.post(`/clients/${id}/activate`);
    if (response.data && typeof response.data === 'object' && 'success' in response.data) {
      if (!response.data.success) {
        throw new Error(response.data.message || 'Erro ao ativar cliente');
      }
    }
  }

  async deactivateClient(id: string): Promise<void> {
    const response = await this.api.post(`/clients/${id}/deactivate`);
    if (response.data && typeof response.data === 'object' && 'success' in response.data) {
      if (!response.data.success) {
        throw new Error(response.data.message || 'Erro ao desativar cliente');
      }
    }
  }

  async processMultipleCnpjs(cnpjs: string[]): Promise<ProcessCnpjResult[]> {
    const request: ProcessMultipleCnpjsRequest = { cnpjs };
    const response = await this.api.post<ProcessMultipleCnpjsResponse>('/clients/process-multiple-cnpjs', request);
    return this.extractData(response);
  }

  async searchAndSaveClient(
    cnpj: string,
    options?: { signal?: AbortSignal; timeoutMs?: number }
  ): Promise<SearchAndSaveClientResponse> {
    const request: SearchAndSaveClientRequest = { cnpj };
    const timeoutMs = options?.timeoutMs ?? CNPJ_SEARCH_TIMEOUT_MS;
    return withRequestTimeout(
      (signal) =>
        this.api
          .post<SearchAndSaveClientResponse>('/clients/search-and-save', request, {
            signal,
            timeout: timeoutMs,
          })
          .then((response) => response.data),
      timeoutMs,
      CNPJ_SEARCH_TIMEOUT_MESSAGE,
      options?.signal
    );
  }

  async getCnpjData(cnpj: string): Promise<ReceitaWsCompanyData> {
    const response = await this.api.get<ReceitaWsCompanyData>(`/ReceitaWs/${cnpj}`);
    return this.extractData(response);
  }

  async validateWhatsApp(formData: FormData): Promise<{ jobId: string; message: string }> {
    const response = await this.api.post('/clients/validate-whatsapp', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return this.extractData(response);
  }

  async enqueueMeuTimHealthCheck(clientIds: string[]): Promise<MeuTimHealthCheckBulkEnqueueResponse> {
    const response = await this.api.post<MeuTimHealthCheckBulkEnqueueResponse>(
      '/clients/meu-tim-health-check/enqueue',
      { clientIds }
    );
    return this.extractData(response);
  }

  /**
   * Verify Meu TIM on-demand (fatia 0 GCM): dispara o login no worker agora
   * e só resolve quando houver um resultado real do portal (nunca "enfileirou
   * = sucesso"). Timeout de cliente folgado (50s) para deixar o teto duro de
   * 45s do servidor responder primeiro com um status explícito.
   */
  async verifyMeuTimHealthNow(clientId: string): Promise<MeuTimHealthCheckOnDemandResponse> {
    const response = await this.api.post<MeuTimHealthCheckOnDemandResponse>(
      `/clients/${clientId}/meu-tim-health-check/verify-now`,
      undefined,
      { timeout: 50000 }
    );
    return this.extractData(response);
  }

  async getValidationStatus(jobId: string): Promise<any> {
    const response = await this.api.get(`/clients/validate-whatsapp/${jobId}/status`);
    return this.extractData(response);
  }
}

// Instância singleton
export const clientService = new ClientService();
